import dayjs from 'dayjs';
import pdfMake from 'pdfmake/build/pdfmake';

import { buildInvoicePdfRows, getInvoiceAmountDue, type PdfRowsInvoice } from '@hopdrive/sdk-ar';

import { GetInvoiceDetailsChargesQuery } from '../queries/getInvoiceDetailsCharges.query.generated';

import DocumentDefinition from '@features/invoices/services/PDFDocumentDefinition';
import { amountDueArgroups, buildAmountObjectFromCharges } from '@features/invoices/services/calcServiceCharges';

// Defining and Using Custom Fonts (matches pdfService.ts)
const pdfMakeFonts = {
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Medium.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-MediumItalic.ttf',
  },
};
pdfMake.fonts = pdfMakeFonts;

/** The account contacts the PDF prints, when the caller has them. */
type InvoicePdfContact = { display_name?: string | null; email?: string | null } | null;
export type InvoicePdfContacts = {
  accountManager?: InvoicePdfContact;
  arRep?: InvoicePdfContact;
};

/** The rows the PDF prints, from the builder the emailed PDF and admin's PDF share. */
export const buildArmovesFromCharges = (invoice: PdfRowsInvoice) => buildInvoicePdfRows(invoice);

/**
 * Generates a PDF for charge-based (new AR) invoices by adapting the data to the
 * legacy shape and reusing the existing PDFDocumentDefinition layout.
 */
export const generateChargesPDF = async (
  invoice?: GetInvoiceDetailsChargesQuery['arinvoices_by_pk'],
  contacts?: InvoicePdfContacts,
) => {
  if (!invoice) return;

  // sdk-ar's Amount Due, read when the PDF is made so it matches what paying the
  // invoice collects. When it cannot be read nothing downloads.
  let amountDue: number;
  try {
    amountDue = await getInvoiceAmountDue(amountDueArgroups(invoice));
  } catch {
    throw new Error('Could not read the amount due, so the PDF was not made');
  }

  const amount = buildAmountObjectFromCharges(invoice, amountDue);
  // Charges have no prepaid concept; PDF only shows this row when > 0
  amount.balanceRemaining = 0;

  const adaptedInvoice = { ...invoice, armoves: buildArmovesFromCharges(invoice) };

  const docDefinition = new DocumentDefinition(adaptedInvoice, amount, contacts);
  const dd = docDefinition.create(adaptedInvoice, amount, contacts);
  const name = `${invoice?.customer?.name?.replace(/ /g, '_')}_Invoice_${dayjs(invoice.start_datetime).add(1, 'day').format('MM-DD-YYYY')}_to_${dayjs(invoice.end_datetime).format('MM-DD-YYYY')}.pdf`;
  pdfMake.createPdf(dd).download(name);
};
